import {
  AdminProject,
  ApiResponse,
  CreateProjectRequest,
  PaginationParams,
  ProjectImage,
  UpdateProjectRequest,
} from "../../types/admin";
import httpClient from "../httpClient";
import { sanitizeArrayResponse, sanitizeDataResponse, ensureArray } from "./helpers";

const sanitizeProject = (project: AdminProject): AdminProject => ({
  ...project,
  techStack: ensureArray(project.techStack),
  images: ensureArray(project.images),
});

export const projectsApi = {
  getAll: async (params?: PaginationParams): Promise<ApiResponse<AdminProject[]>> => {
    const response = await httpClient.get<AdminProject[]>("/projects", params);
    return sanitizeArrayResponse(response, sanitizeProject);
  },

  getById: async (id: string | number): Promise<ApiResponse<AdminProject>> => {
    const response = await httpClient.get<AdminProject>(`/projects/${id}`);
    return sanitizeDataResponse(response, sanitizeProject);
  },

  create: async (project: CreateProjectRequest, files?: File[]): Promise<ApiResponse<AdminProject>> => {
    const formData = new FormData();
    formData.append("Name", project.name);
    formData.append("DescriptionPt", project.descriptionPt);
    formData.append("DescriptionEn", project.descriptionEn);
    formData.append("TechStack", project.techStack.join(","));
    formData.append("ViewUrl", project.viewUrl);
    formData.append("CodeUrl", project.codeUrl);
    if (files) {
      for (const file of files) {
        formData.append("Images", file);
      }
    }
    const response = await httpClient.postFormData<AdminProject>("/projects", formData);
    return sanitizeDataResponse(response, sanitizeProject);
  },

  update: async (id: string | number, project: UpdateProjectRequest, files?: File[]): Promise<ApiResponse<AdminProject>> => {
    const formData = new FormData();
    formData.append("Name", project.name);
    formData.append("DescriptionPt", project.descriptionPt);
    formData.append("DescriptionEn", project.descriptionEn);
    formData.append("TechStack", project.techStack.join(","));
    formData.append("ViewUrl", project.viewUrl);
    formData.append("CodeUrl", project.codeUrl);
    if (files) {
      for (const file of files) {
        formData.append("Images", file);
      }
    }
    const response = await httpClient.putFormData<AdminProject>(`/projects/${id}`, formData);
    return sanitizeDataResponse(response, sanitizeProject);
  },

  delete: (id: string | number): Promise<ApiResponse<void>> =>
    httpClient.delete(`/projects/${id}`),

  getImages: async (projectId: string | number): Promise<ApiResponse<string[]>> =>
    httpClient.get<string[]>(`/projects/${projectId}/images`),

  uploadImage: (projectId: string | number, file: File): Promise<ApiResponse<ProjectImage>> => {
    const formData = new FormData();
    formData.append("image", file);
    return httpClient.postFormData(`/projects/${projectId}/images`, formData);
  },

  getImage: (projectId: string | number, imageId: string | number): Promise<ApiResponse<ProjectImage>> =>
    httpClient.get(`/projects/${projectId}/images/${imageId}`),

  deleteImage: (projectId: string | number, imageId: string | number): Promise<ApiResponse<void>> =>
    httpClient.delete(`/projects/${projectId}/images/${imageId}`),
};
